import { InkBlock } from '@/components/atoms';
import { AppTopbar } from '@/components/organisms';
import { AppShell, Aside } from '@/components/templates';

// page-specific, not reusable: smoke test page for Fase 2 PR 1 primitives.
// Render isolato di tutti i 5 layout primitives + 3 varianti topbar + saggio tipografico.
// Accessibile a /_design/foundation (route registrata in App.tsx).
// Da rimuovere quando i primitives saranno integrati nella vera app (PR 11 Polish).

export default function FoundationSmokePage() {
  return (
    <main className="bg-paper text-ink mx-auto max-w-5xl px-6 py-10">
      <PageHeader />
      <TypographySection />
      <PaletteSection />
      <InkBlockSection />
      <TopbarSection />
      <ShellSection />
    </main>
  );
}

function PageHeader() {
  return (
    <header className="border-rule-strong mb-10 border-b-2 pb-4">
      <p className="text-ink-faint font-display text-xs tracking-[0.18em] uppercase">
        DWR — Fase 2 PR 1
      </p>
      <h1 className="font-display mt-1 text-4xl font-bold tracking-tight">Foundation Smoke</h1>
      <p className="text-ink-soft mt-2 italic">
        Render isolato di tokens DWR + 5 layout primitives. Dev-only.
      </p>
    </header>
  );
}

function TypographySection() {
  return (
    <section className="mb-12">
      <SectionHeading n="01" title="Tipografia DWR" />
      <div className="space-y-2">
        <p className="font-display text-3xl font-bold tracking-tight">Cinzel — heading display</p>
        <p className="font-display tracking-[0.08em] uppercase">
          Cinzel smallcaps — label firma del manuale
        </p>
        <p className="text-base">
          EB Garamond — corpo del testo. Old-style numerals: 1234567890. Questo è il font ereditato
          dal body in tutta l&apos;app.
        </p>
        <p className="text-ink-soft text-base italic">
          EB Garamond italic — istruzioni del manuale, sub-titoli step.
        </p>
        <p className="font-mono text-sm tracking-[0.05em]">
          JetBrains Mono — tag tecnici, item metadata.
        </p>
      </div>
    </section>
  );
}

function PaletteSection() {
  return (
    <section className="mb-12">
      <SectionHeading n="02" title="Palette tema Carta" />
      <div className="grid grid-cols-2 gap-2 sm:grid-cols-4">
        <Swatch name="ink" className="bg-ink text-paper-bright" />
        <Swatch name="ink-soft" className="bg-ink-soft text-paper-bright" />
        <Swatch name="ink-faint" className="bg-ink-faint text-paper-bright" />
        <Swatch name="paper" className="bg-paper border-rule text-ink border" />
        <Swatch name="paper-bright" className="bg-paper-bright border-rule text-ink border" />
        <Swatch name="paper-soft" className="bg-paper-soft text-ink" />
        <Swatch name="paper-deep" className="bg-paper-deep text-ink" />
        <Swatch name="accent-dwr" className="bg-accent-dwr text-paper-bright" />
        <Swatch name="accent-soft" className="bg-accent-soft text-paper-bright" />
        <Swatch name="rule" className="bg-rule text-ink" />
        <Swatch name="rule-strong" className="bg-rule-strong text-paper-bright" />
      </div>
    </section>
  );
}

function InkBlockSection() {
  return (
    <section className="mb-12">
      <SectionHeading n="03" title="InkBlock — atom signature" />
      <div className="flex flex-wrap items-start gap-3">
        <InkBlock>scelta</InkBlock>
        <InkBlock variant="xl">Sali di livello</InkBlock>
      </div>
      <div className="mt-3">
        <InkBlock variant="full">Variante full — block 100% width</InkBlock>
      </div>
      <div className="mt-3">
        <InkBlock asChild>
          <button type="button">asChild → button</button>
        </InkBlock>
      </div>
    </section>
  );
}

function TopbarSection() {
  return (
    <section className="mb-12">
      <SectionHeading n="04" title="AppTopbar — 3 varianti organism" />
      <TopbarVariantPreview variant="default" borderClass="border-rule" />
      <TopbarVariantPreview variant="hub" borderClass="border-rule" />
      <TopbarVariantPreview variant="levelup" borderClass="border-ink" />
    </section>
  );
}

function TopbarVariantPreview({
  variant,
  borderClass,
}: {
  variant: 'default' | 'hub' | 'levelup';
  borderClass: string;
}) {
  return (
    <>
      <div className={`mt-6 border first:mt-0 ${borderClass}`}>
        <AppTopbar
          variant={variant}
          brand={<TopbarBrand variant={variant} />}
          center={<TopbarChar />}
          actions={<TopbarActions levelup={variant === 'levelup'} />}
        />
      </div>
      <p className="text-ink-faint mt-2 font-mono text-xs">variant=&quot;{variant}&quot;</p>
    </>
  );
}

function ShellSection() {
  return (
    <section className="mb-12">
      <SectionHeading n="05" title="AppShell + MainGrid + Aside — templates compositi" />
      <div className="border-rule-strong overflow-hidden border-2">
        <AppShell
          className="min-h-[420px]"
          header={
            <AppTopbar
              variant="default"
              brand={<TopbarBrand variant="default" />}
              center={<TopbarChar />}
              actions={<TopbarActions />}
            />
          }
          main={<ShellMainDemo />}
          aside={<ShellAsideDemo />}
        />
      </div>
      <p className="text-ink-faint mt-2 font-mono text-xs">
        AppShell ⊃ AppTopbar (header) + MainGrid (main + Aside)
      </p>
    </section>
  );
}

function ShellMainDemo() {
  return (
    <div className="space-y-3">
      <InkBlock>step 01 — classe</InkBlock>
      <h2 className="font-display text-2xl font-bold">Scegli la tua classe</h2>
      <p className="text-ink-soft italic">
        Sezione step del builder. In PR 3 questo slot ospiterà{' '}
        <span className="font-mono not-italic">&lt;SectionShell&gt;</span>.
      </p>
      <p>
        Lorem ipsum dolor sit amet, consectetur adipiscing elit. Phasellus vitae ligula quis felis
        pellentesque facilisis. Sed nec leo orci.
      </p>
    </div>
  );
}

function ShellAsideDemo() {
  return (
    <Aside>
      <p className="font-display text-ink-faint mb-3 text-xs tracking-[0.18em] uppercase">
        Step rail
      </p>
      <ol className="space-y-1.5 text-sm">
        <li className="border-ink border-l-2 pl-2 font-medium">01 · Classe</li>
        <li className="text-ink-soft pl-2">02 · Razza</li>
        <li className="text-ink-soft pl-2">03 · Statistiche</li>
        <li className="text-ink-faint pl-2">04 · Equipaggiamento</li>
      </ol>
    </Aside>
  );
}

function SectionHeading({ n, title }: { n: string; title: string }) {
  return (
    <div className="mb-4 flex items-baseline gap-3">
      <span className="font-display text-ink-faint text-xs tracking-[0.18em] uppercase">{n}</span>
      <h2 className="font-display text-xl font-bold tracking-tight">{title}</h2>
    </div>
  );
}

function Swatch({ name, className }: { name: string; className: string }) {
  return (
    <div className={`flex h-16 items-end justify-between px-3 py-2 ${className}`}>
      <span className="font-mono text-xs">{name}</span>
    </div>
  );
}

function TopbarBrand({ variant }: { variant: 'default' | 'hub' | 'levelup' }) {
  const crestBg = variant === 'levelup' ? 'bg-paper-bright text-ink' : 'bg-ink text-paper-bright';
  return (
    <>
      <span className={`font-display grid h-7 w-7 place-items-center text-sm font-bold ${crestBg}`}>
        DW
      </span>
      <span className="font-display text-sm font-bold tracking-[0.08em]">
        Dungeon World Rebrewed
      </span>
    </>
  );
}

function TopbarChar() {
  return (
    <span className="italic">
      <em>Gorm</em> · <strong className="not-italic font-semibold">il Glorioso</strong> ·{' '}
      <span className="opacity-80">Barbaro L1</span>
    </span>
  );
}

function TopbarActions({ levelup = false }: { levelup?: boolean }) {
  const tone = levelup
    ? 'border-paper-bright text-paper-bright hover:bg-white/10'
    : 'border-ink-soft text-ink hover:bg-paper-soft';
  return (
    <button
      type="button"
      className={`font-display border px-2.5 py-1 text-xs tracking-[0.08em] uppercase ${tone}`}
    >
      Esci
    </button>
  );
}
